import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { glossary } from '@sorare/core/src/lib/glossary';

import { useUpdateManagerTeam } from '#src/hooks/so5/useUpdateManagerTeam.ts';

import type { ManagerTeamUpdateDialog_managerTeam } from './__generated__/index.graphql.ts';

const Content = styled.div`
  padding: var(--unit) var(--double-unit);
`;

type Props = {
  managerTeam: ManagerTeamUpdateDialog_managerTeam;
  open: boolean;
  onClose: () => void;
};

export const ManagerTeamUpdateDialog = ({
  managerTeam,
  open,
  onClose,
}: Props) => {
  const [inputValue, setInputValue] = useState(managerTeam.name);
  const { updateManagerTeam } = useUpdateManagerTeam();

  const updateTeam = async () => {
    const result = await updateManagerTeam({
      managerTeamId: managerTeam.id,
      name: inputValue,
    });

    if (result.success) {
      onClose();
    }
  };

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      title={
        <HeadlineM as="h3">
          <FormattedMessage defaultMessage="Edit your Team Name" />
        </HeadlineM>
      }
      onClose={onClose}
      footer={
        <Content>
          <Button
            size="medium"
            fullWidth
            color="secondary"
            onClick={updateTeam}
          >
            <FormattedMessage {...glossary.save} />
          </Button>
        </Content>
      }
    >
      <Content>
        <TextInput
          autoFocus
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
        />
      </Content>
    </Dialog>
  );
};

ManagerTeamUpdateDialog.fragments = {
  managerTeam: gql`
    fragment ManagerTeamUpdateDialog_managerTeam on ManagerTeam {
      id
      name
    }
  ` as TypedDocumentNode<ManagerTeamUpdateDialog_managerTeam>,
};
